import { InternalTreeItem, TaskListItemTypes } from './types';

export const treeItemsDragFormat = 'application/x-griply-tree-items';
const typeMarkerPrefix = 'application/x-griply-tree-item-type/';

export type ForeignTreeItem = Pick<InternalTreeItem, 'sourceId' | 'type'>;

// Browsers lowercase DataTransfer formats, so the marker is matched on the lowercased value.
const typeByMarker = Object.fromEntries(
  Object.values(TaskListItemTypes).map((type) => [
    `${typeMarkerPrefix}${type.toLowerCase()}`,
    type,
  ]),
) as Record<string, TaskListItemTypes>;

const isForeignTreeItem = (value: unknown): value is ForeignTreeItem =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as ForeignTreeItem).sourceId === 'string' &&
  Object.values(TaskListItemTypes).includes((value as ForeignTreeItem).type);

export const writeTreeDragData = (
  dataTransfer: DataTransfer,
  items: ForeignTreeItem[],
) => {
  dataTransfer.setData(
    treeItemsDragFormat,
    JSON.stringify(items.map(({ sourceId, type }) => ({ sourceId, type }))),
  );
  // One marker per type: `dragover` can read `types` but not `getData`.
  new Set(items.map(({ type }) => type)).forEach((type) =>
    dataTransfer.setData(`${typeMarkerPrefix}${type.toLowerCase()}`, type),
  );
};

export const readTreeDragTypes = (
  dataTransfer: DataTransfer,
): TaskListItemTypes[] =>
  Array.from(dataTransfer.types).flatMap(
    (format) => typeByMarker[format.toLowerCase()] ?? [],
  );

export const readTreeDragItems = (
  dataTransfer: DataTransfer,
): ForeignTreeItem[] => {
  const raw = dataTransfer.getData(treeItemsDragFormat);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isForeignTreeItem) : [];
  } catch {
    return [];
  }
};
